import { ModuleColumn, ModuleField } from '../types';
import { buildListColumns } from './recordDisplayUtils';

export type TableModulePrefs = { columns: string[]; filters?: string[] };

export interface ToolbarFilterConfig {
  key: string;
  label: string;
  options: { label: string; value: string }[];
  getValue: (row: Record<string, unknown>) => string;
}

export function getDefaultVisibleFields(allColumns: ModuleColumn[], max = 5): string[] {
  return buildListColumns(allColumns, max).map((c) => c.field);
}

export function applyTableColumnSettings(
  allColumns: ModuleColumn[],
  visibleFields?: string[] | null,
): ModuleColumn[] {
  if (!visibleFields?.length) return buildListColumns(allColumns);
  const map = new Map(allColumns.map((c) => [c.field, c]));
  const result = visibleFields.map((field) => map.get(field)).filter(Boolean) as ModuleColumn[];
  return result.length ? result : buildListColumns(allColumns);
}

export function sanitizeVisibleFields(allColumns: ModuleColumn[], fields: string[]): string[] {
  const allowed = new Set(allColumns.map((c) => c.field));
  const cleaned = fields.filter((f) => allowed.has(f));
  return cleaned.length ? cleaned : getDefaultVisibleFields(allColumns);
}

const SKIP_FILTER_FIELDS = new Set([
  '_id', 'id', 'createdAt', 'updatedAt', 'password', 'photo', 'address',
  'vehicleNumber', 'mobile', 'email', 'tripNumber', 'biltyNumber', 'invoiceNumber',
  'driverName', 'helperName', 'clientName', 'name',
]);

/** Fields a tenant can pick as dropdown filters in Settings → Data Tables. */
export function getFilterableFieldOptions(
  columns: ModuleColumn[],
  formFields: ModuleField[] = [],
): { field: string; header: string }[] {
  const map = new Map<string, string>();
  columns.forEach((col) => {
    if (SKIP_FILTER_FIELDS.has(col.field)) return;
    if (col.format === 'date' || col.format === 'datetime' || col.format === 'currency') return;
    if (col.field === 'status' || col.field === 'paymentStatus') return; // status has its own control
    map.set(col.field, col.header);
  });
  formFields.forEach((f) => {
    if (SKIP_FILTER_FIELDS.has(f.name)) return;
    if (f.type === 'date' || f.type === 'datetime' || f.type === 'textarea' || f.type === 'image' || f.type === 'file' || f.type === 'url') return;
    if (f.name === 'status') return;
    if (!map.has(f.name)) map.set(f.name, f.label);
  });
  return Array.from(map.entries())
    .map(([field, header]) => ({ field, header }))
    .sort((a, b) => a.header.localeCompare(b.header));
}

export function sanitizeFilterFields(
  options: { field: string }[],
  fields: string[],
  max = 5,
): string[] {
  const allowed = new Set(options.map((o) => o.field));
  return fields.filter((f) => allowed.has(f)).slice(0, max);
}

export function getDefaultFilterFields(moduleKey: string): string[] {
  if (moduleKey === 'vehicles') return ['vehicleType', 'fuelType'];
  return [];
}

const VALUE_ALIASES: Record<string, string[]> = {
  vehicleType: ['vehicleType', 'typeName'],
  brand: ['brand', 'makeName'],
  vehicleModel: ['vehicleModel', 'modelName'],
  clientName: ['clientName'],
  ownerType: ['ownerType'],
};

function readFilterValue(row: Record<string, unknown>, field: string, col?: ModuleColumn): string {
  if (col?.searchValue) {
    const v = String(col.searchValue(row) || '').trim();
    if (v) return v;
  }
  const keys = VALUE_ALIASES[field] || [field];
  for (const key of keys) {
    const raw = row[key];
    if (raw == null) continue;
    if (typeof raw === 'object' && raw !== null && 'name' in (raw as object)) {
      const n = String((raw as { name?: string }).name || '').trim();
      if (n) return n;
    }
    const v = String(raw).trim();
    if (v && !/^[a-f0-9]{24}$/i.test(v)) return v;
  }
  return '';
}

/** Build live dropdown filters from Settings-selected fields + current rows. */
export function buildConfiguredToolbarFilters(
  filterFields: string[],
  columns: ModuleColumn[],
  rows: Record<string, unknown>[],
  labels?: Record<string, string>,
): ToolbarFilterConfig[] {
  if (!filterFields.length) return [];
  const colMap = new Map(columns.map((c) => [c.field, c]));
  return filterFields.map((field) => {
    const col = colMap.get(field);
    const label = labels?.[field] || col?.header || field;
    const values = Array.from(new Set(
      rows.map((row) => readFilterValue(row, field, col)).filter(Boolean),
    )).sort((a, b) => a.localeCompare(b));
    return {
      key: field,
      label,
      options: values.map((v) => ({ label: v, value: v })),
      getValue: (row) => readFilterValue(row, field, col),
    };
  });
}
